/*****************************************************************************
 * Open MCT, Copyright (c) 2014-2024, United States Government
 * as represented by the Administrator of the National Aeronautics and Space
 * Administration. All rights reserved.
 *
 * Open MCT is licensed under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0.
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the
 * License for the specific language governing permissions and limitations
 * under the License.
 *
 * Open MCT includes source code licensed under additional open source
 * licenses. See the Open Source Licenses file (LICENSES.md) included with
 * this source code distribution or the Licensing information page available
 * at runtime from the About dialog for additional information.
 *****************************************************************************/

/**
 * Wraps providers that simply match on object identifier namespace
 */
export default class NamespaceProvider {
  #wrappedProvider;
  #namespace;

  constructor(namespace, provider) {
    this.#namespace = namespace;
    this.#wrappedProvider = provider;

    [
      'create',
      'update',
      'delete',
      'observe',
      'supportsSearchType',
      'search',
      'isReadOnly',
      'getAllObjects'
    ].forEach((methodName) => {
      const method = this.#wrappedProvider[methodName];
      if (method !== undefined) {
        this[methodName] = (...methodArguments) => {
          return this.#delegateIfImplemented(method, methodArguments);
        };
      }
    });
  }

  appliesTo(identifier) {
    return Boolean(identifier?.namespace === this.#namespace);
  }

  getWrappedProvider() {
    return this.#wrappedProvider;
  }

  get(identifier) {
    return this.#wrappedProvider.get(identifier);
  }

  #delegateIfImplemented(delegateFunction, delegateArguments) {
    if (delegateFunction !== undefined && typeof delegateFunction === 'function') {
      return delegateFunction.apply(this.#wrappedProvider, delegateArguments);
    } else {
      throw new Error(`Function is not implemented`);
    }
  }
}
